import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { View } from 'react-native';
import Svg, {
  Circle,
  Defs,
  G,
  Line,
  Path,
  Polygon,
  RadialGradient,
  Rect,
  Stop,
} from 'react-native-svg';

import { useThemePalette } from '@/config/theme';

/**
 * Shared, premium pitch-drawing kit for the match center. Both the shot map
 * (vertical attacking half) and the average-positions map (full horizontal
 * pitch) draw their grass + markings from here, so the two surfaces read as one
 * consistent, broadcast-grade pitch (FotMob / SofaScore idiom).
 *
 * Design rules kept identical across both pitches:
 *  - EVEN line weights / opacities (one LINE_W + LINE_OPACITY scale below).
 *  - Correctly-proportioned boxes (penalty box WIDE + shallow at the goal line,
 *    6-yard box inside it, penalty spot + D-arc that hugs the box edge).
 *  - A soft turf vignette + a subtle mown-stripe wash, drawn INSIDE the SVG so
 *    it scales crisply and never fights the dots above it.
 *  - Laid out LTR by the caller, so the pitch never mirrors in RTL.
 */

// One consistent line language for BOTH pitches.
const LINE_W = 1.75; // base marking weight (px)
const GOAL_W = 2.75; // the goal frame is a touch heavier so it reads as the goal
const O_TOUCH = 0.5; // touchline
const O_BOX = 0.5; // penalty box
const O_AREA = 0.42; // 6-yard area
const O_ARC = 0.42; // D-arc / centre arc
const O_GOAL = 0.85; // goal frame
const O_SPOT = 0.7; // penalty / centre spot
const SPOT_R = 1.7;

// ---------------------------------------------------------------------------
// Turf — a soft floodlit grass: a faint top→bottom vignette + a couple of mown
// stripes. Drawn as plain RN views (cheap) so it sits under the SVG markings.
// `orientation` mows the stripes the right way for each pitch.
// ---------------------------------------------------------------------------

export function PitchTurf({
  orientation = 'vertical',
}: {
  /** 'vertical' = a tall half (stripes run across); 'horizontal' = a full pitch
   *  (stripes run down the length). */
  orientation?: 'vertical' | 'horizontal';
}) {
  // Three barely-there mown bands give the grass depth without banding hard.
  const stripe = 'rgba(255,255,255,0.025)';
  const isVertical = orientation === 'vertical';
  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} pointerEvents="none">
      {/* Mown stripes — alternating faint bands. */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: isVertical ? 'column' : 'row' }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <View key={i} style={{ flex: 1, backgroundColor: i % 2 === 0 ? stripe : 'transparent' }} />
        ))}
      </View>
      {/* Floodlight vignette — brightest where the action is (top for a half,
          centre for a full pitch), fading to a darker rim for depth. */}
      <LinearGradient
        colors={
          isVertical
            ? ['rgba(255,255,255,0.06)', 'rgba(0,0,0,0.03)', 'rgba(0,0,0,0.20)']
            : ['rgba(0,0,0,0.16)', 'rgba(255,255,255,0.05)', 'rgba(0,0,0,0.16)']
        }
        locations={isVertical ? [0, 0.55, 1] : [0, 0.5, 1]}
        start={isVertical ? { x: 0.5, y: 0 } : { x: 0, y: 0.5 }}
        end={isVertical ? { x: 0.5, y: 1 } : { x: 1, y: 0.5 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        pointerEvents="none"
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Vertical attacking HALF-pitch — the shot map surface. Goal + 6-yard box +
// penalty box + spot + D-arc at the TOP (the attacked goal); halfway line +
// centre-circle arc at the BOTTOM. Sized in pixels off the measured box so the
// markings line up exactly with the pixel-mapped dots drawn above.
// ---------------------------------------------------------------------------

export function HalfPitchMarkings({
  width,
  height,
  inset,
}: {
  width: number;
  height: number;
  inset: number;
}) {
  const palette = useThemePalette();
  const line = palette.pitchLine;
  const m = inset;
  const innerW = width - m * 2;
  const innerH = height - m * 2;
  const cx = width / 2;

  // Penalty box: WIDE + shallow at the goal edge. The 6-yard area + goal sit
  // inside it, proportioned off the box so they always nest correctly.
  const boxW = Math.min(innerW * 0.66, 240);
  const boxH = Math.min(innerH * 0.3, 96);
  const areaW = boxW * 0.46;
  const areaH = boxH * 0.44;
  const goalW = areaW * 0.62;
  const goalDepth = Math.max(5, boxH * 0.11);
  const boxLeft = cx - boxW / 2;
  const areaLeft = cx - areaW / 2;
  const goalLeft = cx - goalW / 2;
  const penFromGoal = boxH * 0.68; // penalty spot, in from the goal line
  // D-arc radius; the arc only shows the part beyond the box edge.
  const arcR = boxW * 0.2;
  const arcDX = boxW * 0.16;
  const centreR = Math.min(innerW * 0.16, 70);

  return (
    <Svg width={width} height={height} style={{ position: 'absolute', top: 0, left: 0 }} pointerEvents="none">
      {/* Touchline */}
      <Rect x={m} y={m} width={innerW} height={innerH} rx={7} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_TOUCH} strokeLinejoin="round" />

      {/* Halfway line (bottom) + centre-circle arc curving up into the half. */}
      <Line x1={m} y1={m + innerH} x2={m + innerW} y2={m + innerH} stroke={line} strokeWidth={LINE_W} opacity={O_TOUCH} />
      <Path
        d={`M ${cx - centreR} ${m + innerH} A ${centreR} ${centreR} 0 0 1 ${cx + centreR} ${m + innerH}`}
        fill="none"
        stroke={line}
        strokeWidth={LINE_W}
        opacity={O_ARC}
      />
      <Circle cx={cx} cy={m + innerH} r={SPOT_R} fill={line} opacity={O_SPOT} />

      {/* TOP end — the attacked goal. */}
      <Rect x={boxLeft} y={m} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_BOX} strokeLinejoin="round" />
      <Rect x={areaLeft} y={m} width={areaW} height={areaH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_AREA} strokeLinejoin="round" />
      <Circle cx={cx} cy={m + penFromGoal} r={SPOT_R} fill={line} opacity={O_SPOT} />
      {/* D-arc just below the penalty box (only the bulge past the box shows). */}
      <Path
        d={`M ${cx - arcDX} ${m + boxH} A ${arcR} ${arcR} 0 0 0 ${cx + arcDX} ${m + boxH}`}
        fill="none"
        stroke={line}
        strokeWidth={LINE_W}
        opacity={O_ARC}
      />
      {/* Goal frame — a crisp open net just outside the line, heavier weight. */}
      <Rect x={goalLeft} y={m - goalDepth} width={goalW} height={goalDepth} fill="none" stroke={line} strokeWidth={GOAL_W} opacity={O_GOAL} strokeLinejoin="round" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// Full HORIZONTAL pitch — the average-positions surface. Touchline, halfway line
// + centre circle + spot, and a penalty box (with 6-yard area, spot, D-arc and
// goal) at EACH end. Same line language as the half-pitch above.
// ---------------------------------------------------------------------------

export function FullPitchMarkings({
  width,
  height,
  inset,
}: {
  width: number;
  height: number;
  inset: number;
}) {
  const palette = useThemePalette();
  const line = palette.pitchLine;
  const cx = width / 2;
  const cy = height / 2;
  const m = inset;
  const innerW = width - m * 2;
  const innerH = height - m * 2;

  // Penalty box: tall + shallow at each goal line. 6-yard area nests inside.
  const boxH = Math.min(innerH * 0.6, 152);
  const boxW = Math.min(innerW * 0.16, 66);
  const areaH = boxH * 0.52;
  const areaW = boxW * 0.42;
  const goalDepth = Math.max(5, boxW * 0.16);
  const goalH = areaH * 0.62;
  const boxTop = cy - boxH / 2;
  const areaTop = cy - areaH / 2;
  const goalTop = cy - goalH / 2;
  const penFromGoal = boxW * 0.68;
  const arcR = boxH * 0.22;
  const arcDY = boxH * 0.16;
  const centreR = Math.min(innerH * 0.18, 48);

  return (
    <Svg width={width} height={height} style={{ position: 'absolute', top: 0, left: 0 }} pointerEvents="none">
      {/* Touchline */}
      <Rect x={m} y={m} width={innerW} height={innerH} rx={7} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_TOUCH} strokeLinejoin="round" />

      {/* Halfway line + centre circle + spot. */}
      <Line x1={cx} y1={m} x2={cx} y2={m + innerH} stroke={line} strokeWidth={LINE_W} opacity={O_TOUCH} />
      <Circle cx={cx} cy={cy} r={centreR} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_ARC} />
      <Circle cx={cx} cy={cy} r={SPOT_R} fill={line} opacity={O_SPOT} />

      {/* LEFT end (away attacks here). */}
      <Rect x={m} y={boxTop} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_BOX} strokeLinejoin="round" />
      <Rect x={m} y={areaTop} width={areaW} height={areaH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_AREA} strokeLinejoin="round" />
      <Circle cx={m + penFromGoal} cy={cy} r={SPOT_R} fill={line} opacity={O_SPOT} />
      <Path
        d={`M ${m + boxW} ${cy - arcDY} A ${arcR} ${arcR} 0 0 1 ${m + boxW} ${cy + arcDY}`}
        fill="none"
        stroke={line}
        strokeWidth={LINE_W}
        opacity={O_ARC}
      />
      <Rect x={m - goalDepth} y={goalTop} width={goalDepth} height={goalH} fill="none" stroke={line} strokeWidth={GOAL_W} opacity={O_GOAL} strokeLinejoin="round" />

      {/* RIGHT end (home attacks here). */}
      <Rect x={m + innerW - boxW} y={boxTop} width={boxW} height={boxH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_BOX} strokeLinejoin="round" />
      <Rect x={m + innerW - areaW} y={areaTop} width={areaW} height={areaH} fill="none" stroke={line} strokeWidth={LINE_W} opacity={O_AREA} strokeLinejoin="round" />
      <Circle cx={m + innerW - penFromGoal} cy={cy} r={SPOT_R} fill={line} opacity={O_SPOT} />
      <Path
        d={`M ${m + innerW - boxW} ${cy - arcDY} A ${arcR} ${arcR} 0 0 0 ${m + innerW - boxW} ${cy + arcDY}`}
        fill="none"
        stroke={line}
        strokeWidth={LINE_W}
        opacity={O_ARC}
      />
      <Rect x={m + innerW} y={goalTop} width={goalDepth} height={goalH} fill="none" stroke={line} strokeWidth={GOAL_W} opacity={O_GOAL} strokeLinejoin="round" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// The football glyph — a clean SVG ball that reads instantly as a REAL football.
// A radial-shaded white sphere with classic pentagons + seams and a soft specular
// highlight, sized in a fixed 24×24 viewBox so it scales crisply. This is the
// shared match-center football (the live ball tracker uses the same idiom), so a
// GOAL on the shot map is unmistakably a ball, not just a coloured dot.
// ---------------------------------------------------------------------------

export const PitchFootball = React.memo(function PitchFootball({ size }: { size: number }) {
  const seam = 'rgba(17,20,34,0.78)';
  const edge = 'rgba(17,20,34,0.30)';
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" pointerEvents="none">
      <Defs>
        <RadialGradient id="ballBody" cx="40%" cy="34%" r="72%">
          <Stop offset="0%" stopColor="#FFFFFF" />
          <Stop offset="62%" stopColor="#F4F6FB" />
          <Stop offset="100%" stopColor="#D6DAE6" />
        </RadialGradient>
      </Defs>
      {/* Sphere body with a soft radial shade so it reads round, not flat. */}
      <Circle cx={12} cy={12} r={11} fill="url(#ballBody)" stroke={edge} strokeWidth={0.75} />
      <G fill={seam}>
        {/* Centre pentagon — the classic football mark. */}
        <Polygon points="12,7.6 15.1,9.9 13.9,13.5 10.1,13.5 8.9,9.9" />
        {/* Faint partial pentagons + short seams around the rim for the panelled
            look (kept subtle so the ball stays clean at small sizes). */}
        <Polygon points="12,2.8 14.1,4.3 13.3,6.5 10.7,6.5 9.9,4.3" opacity={0.4} />
        <Polygon points="3.9,10.8 6.2,11.3 6.6,13.7 4.6,15.1 2.9,13.2" opacity={0.4} />
        <Polygon points="20.1,10.8 21.1,13.2 19.4,15.1 17.4,13.7 17.8,11.3" opacity={0.4} />
      </G>
      <G stroke={seam} strokeWidth={0.7} opacity={0.45}>
        <Line x1={12} y1={6.5} x2={12} y2={7.6} />
        <Line x1={9.1} y1={9.9} x2={6.6} y2={11.3} />
        <Line x1={14.9} y1={9.9} x2={17.4} y2={11.3} />
        <Line x1={10.1} y1={13.5} x2={8.5} y2={15.6} />
        <Line x1={13.9} y1={13.5} x2={15.5} y2={15.6} />
      </G>
      {/* Tiny specular highlight, top-left. */}
      <Circle cx={8.8} cy={8.4} r={1.7} fill="rgba(255,255,255,0.95)" />
    </Svg>
  );
});
